// ArenaTouchControls — on-screen joystick + action buttons for touch devices.
//
// This is the mobile counterpart to the physical keyboard + mouse controls that
// drive the Sports Arena on desktop:
//
//   • A right-thumb virtual joystick (nipplejs) that dispatches synthetic
//     `keydown`/`keyup` events for W / A / S / D. The Sports Arena already
//     listens on `window` for those exact keys (see SportsArena.jsx line ~1440),
//     so no changes are required in the arena scene itself. The joystick only
//     appears in football when the visitor is playing a moving role
//     (WR / RB / DB) — the QB role uses tap-to-throw and doesn't need it.
//
//   • A large "FIRE" button anchored bottom-right. This invokes the fire
//     function registered by SportsArena on `arenaApi.touchFireRef`. The label
//     switches between SHOOT / KICK / SNAP / THROW to match the currently
//     active sport and (for football) the current snap phase.
//
//   • Role quick-swap chips (only in football on touch) so mobile visitors can
//     switch QB / WR / RB / DB without opening the ArenaHUD.
//
// The whole panel is fixed to the bottom-right corner so it never overlaps the
// walk-mode MobileJoystick (bottom-left) or the ArenaHUD (bottom-left column).

import React, { useEffect, useMemo, useRef } from "react";
import nipplejs from "nipplejs";

import { COLORS } from "../constants";
import { FOOTBALL_ROLES, FOOTBALL_ROLE_ORDER } from "../arena/arenaState";

// ---------------------------------------------------------------------------
// Synthetic keyboard dispatch — the arena already handles keydown/keyup on
// window; we drive it from the joystick by faking those events. We track
// which keys are currently "held" so we only dispatch transitions.
// ---------------------------------------------------------------------------
function makeKeyDriver() {
  const held = { w: false, a: false, s: false, d: false };

  function set(key, down) {
    if (held[key] === down) return;
    held[key] = down;
    const evt = new KeyboardEvent(down ? "keydown" : "keyup", {
      key,
      bubbles: true,
    });
    window.dispatchEvent(evt);
  }

  function apply(vecX, vecY) {
    // Deadzone so we don't chatter when the user rests the thumb.
    const DEAD = 0.28;
    // Positive Y (nipplejs) = up = forward = W. Negative Y = S. Positive X = D.
    set("w", vecY > DEAD);
    set("s", vecY < -DEAD);
    set("d", vecX > DEAD);
    set("a", vecX < -DEAD);
  }

  function release() {
    set("w", false);
    set("a", false);
    set("s", false);
    set("d", false);
  }

  return { apply, release };
}

// ---------------------------------------------------------------------------
export default function ArenaTouchControls({ arenaApi, visible }) {
  const stickRef = useRef(null);
  const managerRef = useRef(null);
  const driverRef = useRef(null);

  const { sport, role, setRole, touchFireRef, swingDirRef } = arenaApi;
  const [batAim, setBatAim] = React.useState(0); // -1 oppo, 0 centre, +1 pull

  // Keep the exposed swingDirRef in sync with the local UI state so the
  // baseball hit logic sees the latest choice — but only for baseball; for
  // other sports the ref stays 0.
  React.useEffect(() => {
    if (!swingDirRef) return;
    swingDirRef.current = sport === "baseball" ? batAim : 0;
  }, [batAim, swingDirRef, sport]);

  // Desktop shortcut keys — Q = pull left, S = straightaway, E = oppo field.
  // (We avoid A because it's the WASD walk key; using S keeps the row
  // together and doesn't fight movement input.)
  React.useEffect(() => {
    if (sport !== "baseball") return;
    const onKey = (e) => {
      // Don't hijack input while the user is typing in a form field.
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key === "q" || e.key === "Q") setBatAim(1);
      else if (e.key === "s" || e.key === "S") setBatAim(0);
      else if (e.key === "e" || e.key === "E") setBatAim(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sport]);

  // The joystick only makes sense in football roles that involve running
  // (WR / RB / DB). For QB and for basketball / soccer, tapping the FIRE
  // button is the whole game.
  const needsJoystick =
    visible && sport === "football" && role !== "qb";

  useEffect(() => {
    if (!needsJoystick || !stickRef.current) return;
    driverRef.current = makeKeyDriver();

    const m = nipplejs.create({
      zone: stickRef.current,
      mode: "static",
      position: { left: "50%", top: "50%" },
      color: COLORS.gold,
      size: 108,
      restOpacity: 0.55,
    });
    managerRef.current = m;

    m.on("move", (_, data) => {
      if (!data || !data.vector) return;
      driverRef.current.apply(data.vector.x, data.vector.y);
    });
    m.on("end", () => {
      driverRef.current.release();
    });

    return () => {
      try {
        m.destroy();
      } catch (e) {
        /* ignore */
      }
      managerRef.current = null;
      // Make sure we don't leave keys stuck in the "down" state when the
      // joystick disappears (e.g. visitor swaps role from RB → QB).
      if (driverRef.current) driverRef.current.release();
      driverRef.current = null;
    };
  }, [needsJoystick]);

  // Label the fire button by sport + snap phase (football only).
  const fireLabel = useMemo(() => {
    if (sport === "basketball") return "SHOOT";
    if (sport === "soccer") return "KICK";
    // Football — we don't know the snap phase from here (it lives inside
    // SportsArena as a ref) so we pick a label that reads correctly per role.
    if (role === "qb") return "SNAP / THROW";
    if (role === "db") return "TACKLE";
    return "CATCH"; // wr / rb — catches are automatic; button is a fallback fire
  }, [sport, role]);

  // Base visibility gate for the touch-only controls (joystick + FIRE).
  // Bat-aim chips always render when sport==="baseball" regardless of
  // pointer type — desktop users use these buttons instead of Q/A/E keys.
  const showTouchOnly = visible;
  const showBatAim = sport === "baseball";
  if (!showTouchOnly && !showBatAim) return null;

  const handleFire = () => {
    const fn = touchFireRef && touchFireRef.current && touchFireRef.current.fire;
    if (typeof fn === "function") fn();
  };

  return (
    <div
      data-testid="arena-touch-controls"
      style={{
        position: "fixed",
        right: 20,
        bottom: 24,
        // Action buttons MUST sit above the ArenaHUD (z:6), the docked
        // TopNav (z:5), the bottom-left utility pills (z:40), and any
        // other HUD chrome — but stay BELOW modal overlays (z:91/92).
        zIndex: 60,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 12,
        pointerEvents: "none", // container is a shell; children re-enable
        touchAction: "none",
      }}
    >
      {/* Bat-path aim chips — baseball only. Player picks their swing
          direction BEFORE the FIRE tap so timing + aim compose. */}
      {sport === "baseball" && (
        <div
          data-testid="arena-bat-aim"
          style={{
            display: "flex",
            gap: 6,
            pointerEvents: "auto",
            padding: "4px 6px",
            borderRadius: 14,
            background: "rgba(11,21,48,0.68)",
            border: "1px solid rgba(230,180,80,0.35)",
            backdropFilter: "blur(8px)",
          }}
        >
          {[
            { v: 1, label: "PULL", tid: "bat-aim-pull" },
            { v: 0, label: "STRT", tid: "bat-aim-straight" },
            { v: -1, label: "OPPO", tid: "bat-aim-oppo" },
          ].map((opt) => {
            const active = batAim === opt.v;
            return (
              <button
                key={opt.v}
                data-testid={opt.tid}
                onClick={() => setBatAim(opt.v)}
                style={{
                  padding: "6px 10px",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  border: `1px solid ${active ? COLORS.gold : "rgba(230,180,80,0.35)"}`,
                  background: active
                    ? "linear-gradient(180deg,#e6b450 0%,#c8933a 100%)"
                    : "rgba(11,21,48,0.35)",
                  color: active ? "#111827" : "#f3ead4",
                  borderRadius: 10,
                  cursor: "pointer",
                  touchAction: "manipulation",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Role quick-swap chips — football + touch only */}
      {sport === "football" && showTouchOnly && (
        <div
          data-testid="arena-touch-roles"
          style={{
            display: "flex",
            gap: 6,
            pointerEvents: "auto",
            padding: "4px 6px",
            borderRadius: 14,
            background: "rgba(11,21,48,0.68)",
            border: `1px solid ${COLORS.goldSoft || "rgba(230,180,80,0.35)"}`,
            backdropFilter: "blur(8px)",
          }}
        >
          {FOOTBALL_ROLE_ORDER.map((r) => {
            const active = r === role;
            const meta = FOOTBALL_ROLES.find((x) => x.id === r);
            return (
              <button
                key={r}
                type="button"
                data-testid={`arena-touch-role-${r}`}
                onClick={() => setRole(r)}
                style={{
                  padding: "6px 10px",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  border: `1px solid ${active ? COLORS.gold : "rgba(230,180,80,0.35)"}`,
                  background: active
                    ? "linear-gradient(180deg,#e6b450 0%,#c8933a 100%)"
                    : "rgba(11,21,48,0.35)",
                  color: active ? "#111827" : "#f3ead4",
                  borderRadius: 10,
                  cursor: "pointer",
                  touchAction: "manipulation",
                }}
              >
                {(meta && meta.label) || r.toUpperCase()}
              </button>
            );
          })}
        </div>
      )}

      {/* Joystick — football running roles only */}
      {needsJoystick && showTouchOnly && (
        <div
          data-testid="arena-touch-joystick"
          ref={stickRef}
          style={{
            width: 150,
            height: 150,
            pointerEvents: "auto",
            touchAction: "none",
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 50% 50%, rgba(230,180,80,0.10) 0%, rgba(11,21,48,0.55) 65%)",
            border: "1px solid rgba(230,180,80,0.35)",
            backdropFilter: "blur(6px)",
          }}
        />
      )}

      {/* FIRE button — always shown while arena is open on touch */}
      {showTouchOnly && (
        <button
          type="button"
          data-testid="arena-touch-fire"
          onClick={handleFire}
          onTouchStart={(e) => {
            // Prevent the synthetic click that follows a touch — we already
            // fire on `onClick` and don't want double-fires.
            e.preventDefault();
            handleFire();
          }}
          style={{
            pointerEvents: "auto",
            touchAction: "manipulation",
            width: 108,
            height: 108,
            borderRadius: "50%",
            border: `2px solid ${COLORS.gold}`,
            background:
              "radial-gradient(circle at 30% 25%, #f7dc95 0%, #e6b450 45%, #a97521 100%)",
            color: "#0b1530",
            fontWeight: 900,
            fontSize: 15,
            letterSpacing: 0.6,
            boxShadow:
              "0 12px 28px rgba(0,0,0,0.45), inset 0 -6px 12px rgba(0,0,0,0.25), 0 0 0 6px rgba(230,180,80,0.14)",
            cursor: "pointer",
            lineHeight: 1.1,
            textAlign: "center",
            padding: 6,
          }}
        >
          {fireLabel}
        </button>
      )}
    </div>
  );
}
